/**
 * Typisierte Felder in Journal und Reflexion (F-32) - rein und getestet.
 * Freitext liegt wie bisher in `reflexion.eigene`, alles andere in
 * `reflexion.werte`. Leer heisst: kein Wert (nicht 0, nicht „Nein“).
 */
import type { FeldTyp, FeldWert, Reflexion, ReflexionsFrageDef } from '@/domain/types';

export const FELD_TYPEN: { typ: FeldTyp | 'bewertung'; label: string }[] = [
  { typ: 'text', label: 'Freitext kurz' },
  { typ: 'langtext', label: 'Freitext lang' },
  { typ: 'bewertung', label: 'Bewertung 1–10' },
  { typ: 'janein', label: 'Ja/Nein' },
  { typ: 'zahl', label: 'Zahl mit Einheit' },
  { typ: 'uhrzeit', label: 'Uhrzeit' },
  { typ: 'checkliste', label: 'Checkliste' },
];

export const typVon = (f: Pick<ReflexionsFrageDef, 'typ'>): FeldTyp => f.typ ?? 'langtext';
export const istText = (f: Pick<ReflexionsFrageDef, 'typ'>) => typVon(f) === 'text' || typVon(f) === 'langtext';

/** Ist ein Wert „leer“? */
export function leer(v: FeldWert | undefined): boolean {
  if (v === undefined) return true;
  if (typeof v === 'string') return v.trim() === '';
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === 'number') return !Number.isFinite(v);
  return false;
}

/** Wert passend zum Typ - Unpassendes faellt weg. */
export function gueltig(typ: FeldTyp, v: FeldWert | undefined): FeldWert | undefined {
  if (leer(v)) return undefined;
  if (typ === 'zahl') return typeof v === 'number' ? v : undefined;
  if (typ === 'janein') return typeof v === 'boolean' ? v : undefined;
  if (typ === 'uhrzeit') return typeof v === 'string' && /^([01]\d|2[0-3]):[0-5]\d$/.test(v) ? v : undefined;
  if (typ === 'checkliste') return Array.isArray(v) ? [...new Set(v.map((x) => x.trim()).filter(Boolean))] : undefined;
  return undefined;
}

export const wertVon = (r: Reflexion | undefined, id: string): FeldWert | undefined => r?.werte?.[id];

export function mitWert(r: Reflexion | undefined, id: string, v: FeldWert | undefined): Reflexion {
  const werte = { ...r?.werte };
  if (leer(v)) delete werte[id];
  else werte[id] = v!;
  return { ...r, werte };
}

/** Werte bereinigen (beim Speichern) - nur gueltige zu bekannten oder schon gespeicherten Feldern. */
export function werteBereinigt(werte: Record<string, FeldWert> | undefined, felder: ReflexionsFrageDef[]): Record<string, FeldWert> | undefined {
  if (!werte) return undefined;
  const out: Record<string, FeldWert> = {};
  for (const [id, v] of Object.entries(werte)) {
    const f = felder.find((x) => x.id === id);
    const g = f ? gueltig(typVon(f), v) : leer(v) ? undefined : v;
    if (g !== undefined) out[id] = g;
  }
  return Object.keys(out).length ? out : undefined;
}

/** Lesbarer Text eines Werts - fuer Suche, Rueckblick und alte Eintraege. */
export function wertText(f: Pick<ReflexionsFrageDef, 'typ' | 'einheit'>, v: FeldWert | undefined): string {
  if (v === undefined) return '';
  if (typeof v === 'boolean') return v ? 'Ja' : 'Nein';
  if (typeof v === 'number') return `${v.toLocaleString('de-DE')}${f.einheit ? ` ${f.einheit}` : ''}`;
  if (Array.isArray(v)) return v.join(', ');
  return v;
}

/** Punkte einer Checkliste fuer einen Eintrag: aktuelle plus damals abgehakte, die es nicht mehr gibt. */
export function checklistePunkte(f: Pick<ReflexionsFrageDef, 'punkte'>, v: FeldWert | undefined): { text: string; an: boolean; alt: boolean }[] {
  const an = new Set(Array.isArray(v) ? v : []);
  const aktuell = (f.punkte ?? []).map((t) => ({ text: t, an: an.has(t), alt: false }));
  const weg = [...an].filter((t) => !(f.punkte ?? []).includes(t)).map((t) => ({ text: t, an: true, alt: true }));
  return [...aktuell, ...weg];
}

/** Verlauf eines Zahl- oder Ja/Nein-Felds (Statistik, F-32 B). */
export function feldReihe(eintraege: { date: string; reflexion?: Reflexion }[], id: string): { date: string; wert: number | boolean }[] {
  return eintraege.flatMap((e) => { const v = e.reflexion?.werte?.[id]; return typeof v === 'number' || typeof v === 'boolean' ? [{ date: e.date, wert: v }] : []; })
    .sort((a, b) => a.date.localeCompare(b.date));
}

/** Ja-Quote in Prozent (gerundet) - `null` ohne Antworten. */
export function jaQuote(reihe: { wert: number | boolean }[]): number | null {
  const b = reihe.filter((x) => typeof x.wert === 'boolean');
  return b.length ? Math.round((b.filter((x) => x.wert === true).length / b.length) * 100) : null;
}
